import api from '@/lib/api';
import type { PaginatedResponse, StockAdjustmentReason } from '@pms/shared';

// ─── Query / payload / response types ──────────────────────────────────────────

export interface ListRetailStockQuery {
  propertyId: string;
  search?: string | undefined;
}

/** One per-product stock row from GET /retail/stock. */
export interface StockRow {
  productId: string;
  name: string;
  sku: string;
  stockOnHand: number;
  lastAdjustedAt?: string | null;
}

export interface AdjustStockPayload {
  productId: string;
  /** Signed delta: positive = add, negative = remove. */
  delta: number;
  reason: StockAdjustmentReason;
  note?: string | undefined;
}

export interface AdjustStockResponse {
  productId: string;
  stockOnHand: number;
}

// ─── API functions ────────────────────────────────────────────────────────────

export async function fetchRetailStock(
  query: ListRetailStockQuery,
): Promise<PaginatedResponse<StockRow>> {
  const { data } = await api.get<PaginatedResponse<StockRow>>('/retail/stock', {
    params: query,
  });
  return data;
}

export async function adjustRetailStock(
  payload: AdjustStockPayload,
): Promise<AdjustStockResponse> {
  // The API exposes POST /v1/retail/stock/:productId/adjust with a {direction,
  // quantity} body; the UI works in a signed delta, so translate at the edge.
  const { productId, delta, reason, note } = payload;
  const body = {
    direction: delta >= 0 ? 'add' : 'remove',
    quantity: Math.abs(delta),
    reason,
    ...(note ? { note } : {}),
  };
  const { data } = await api.post<AdjustStockResponse>(
    `/retail/stock/${productId}/adjust`,
    body,
  );
  return data;
}
